/**
 * Session Manager - Single Session Enforcement
 * 
 * Prevents the same user from being logged in on multiple browsers/devices
 * simultaneously. Each login generates a unique session token stored in both
 * localStorage (browser-scoped) and Firestore (global). A real-time listener
 * detects when another login overwrites the token, triggering forced sign-out
 * on the older session.
 * 
 * Design:
 * - localStorage is shared between tabs in the same browser, so multiple tabs
 *   share one session token and don't conflict with each other.
 * - A different browser/device generates a different token, which overwrites
 *   the Firestore document and kicks the old session.
 */

import { doc, setDoc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, getArtifactProjectId } from '../firebaseInit';
import { CURRENT_ENV } from '../crossEnvFirebase';

const DEBUG = process.env.REACT_APP_DEBUG_AUTH === '1' || process.env.NODE_ENV === 'development';

const debugLog = (message, ...args) => {
  if (DEBUG) {
    console.log(`🔐 [SESSION-MANAGER] ${message}`, ...args);
  }
};

// ---------------------------------------------------------------------------
// Token generation & localStorage helpers
// ---------------------------------------------------------------------------

/**
 * Generate a unique session token.
 */
export const generateSessionToken = () => {
  const timestamp = Date.now();
  const random1 = Math.random().toString(36).substring(2, 14);
  const random2 = Math.random().toString(36).substring(2, 14);
  return `${timestamp}_${random1}_${random2}`;
};

const getStorageKey = (uid) => `treegenie_session_${uid}`;

/**
 * Read the current session token from localStorage.
 * Returns null if no token exists.
 */
export const getLocalSessionToken = (uid) => {
  try {
    return localStorage.getItem(getStorageKey(uid));
  } catch {
    return null;
  }
};

/**
 * Write a session token to localStorage (synchronous).
 */
export const setLocalSessionToken = (uid, token) => {
  try {
    localStorage.setItem(getStorageKey(uid), token);
  } catch (e) {
    console.warn('⚠️ [SESSION-MANAGER] Failed to write session token to localStorage:', e);
  }
};

/**
 * Remove the session token from localStorage.
 */
export const clearLocalSessionToken = (uid) => {
  try {
    localStorage.removeItem(getStorageKey(uid));
  } catch {
    // Ignore
  }
};

// ---------------------------------------------------------------------------
// Firestore helpers
// ---------------------------------------------------------------------------

const getSessionDocRef = (uid) => {
  const appId = getArtifactProjectId();
  return doc(db, `artifacts/${appId}/activeSessions/${uid}`);
};

const getPresenceDocRef = (uid) => {
  const appId = getArtifactProjectId();
  return doc(db, `artifacts/${appId}/userPresence/${uid}`);
};

/**
 * Write the session token to Firestore.
 * This is the "source of truth" that other sessions listen to.
 */
export const writeSessionToFirestore = async (uid, email, sessionToken) => {
  const sessionRef  = getSessionDocRef(uid);
  const presenceRef = getPresenceDocRef(uid);

  debugLog('Writing session to Firestore:', {
    uid,
    email: email ? email.split('@')[0] + '@...' : 'unknown',
    token: sessionToken.substring(0, 20) + '...',
  });

  await Promise.all([
    setDoc(sessionRef, {
      sessionToken,
      email: email || null,
      loginAt: serverTimestamp(),
      lastActiveAt: serverTimestamp(),
      userAgent: navigator.userAgent.substring(0, 200),
    }),
    // Lightweight presence doc - no session token, safe for cross-env reads
    setDoc(presenceRef, {
      email: email || null,
      lastActiveAt: serverTimestamp(),
      env: CURRENT_ENV,
    }, { merge: true }),
  ]);

  debugLog('Session written to Firestore successfully');
};

// ---------------------------------------------------------------------------
// High-level API
// ---------------------------------------------------------------------------

/**
 * Register a new session for the user.
 * Called on explicit login. Generates a new token, stores it locally and in
 * Firestore. Returns the token.
 */
export const registerSession = async (uid, email) => {
  const token = generateSessionToken();

  // Synchronous local write (available immediately for the listener)
  setLocalSessionToken(uid, token);

  // Asynchronous Firestore write
  await writeSessionToFirestore(uid, email, token);

  debugLog('Session registered:', { uid, token: token.substring(0, 20) + '...' });
  return token;
};

/**
 * Ensure a session token exists for the current user.
 * If one already exists in localStorage, it is reused (e.g. page refresh).
 * If not, a new one is generated and written (e.g. first load after signup).
 * Returns the token.
 */
export const ensureSession = async (uid, email) => {
  let token = getLocalSessionToken(uid);

  if (token) {
    debugLog('Existing session token found in localStorage');
    return token;
  }

  // No local token — register a fresh session
  debugLog('No local session token found, registering new session');
  token = await registerSession(uid, email);
  return token;
};

/**
 * Set up a real-time listener on the user's session document.
 * When the Firestore token differs from the local token, the onConflict
 * callback is invoked (the user should be signed out).
 *
 * Returns an unsubscribe function.
 */
export const listenForSessionConflict = (uid, onConflict, onError) => {
  const sessionRef = getSessionDocRef(uid);

  debugLog('Setting up session conflict listener for uid:', uid);

  // Skip the very first snapshot to avoid false positives from stale data
  // or race conditions during registration.
  let isFirstSnapshot = true;

  const unsubscribe = onSnapshot(
    sessionRef,
    (snapshot) => {
      if (isFirstSnapshot) {
        isFirstSnapshot = false;
        debugLog('Initial session snapshot received — skipping');
        return;
      }

      if (!snapshot.exists()) {
        debugLog('Session document deleted — ignoring');
        return;
      }

      const data = snapshot.data();
      const localToken = getLocalSessionToken(uid);

      debugLog('Session document changed:', {
        firestoreToken: data.sessionToken?.substring(0, 20) + '...',
        localToken: localToken?.substring(0, 20) + '...',
        match: data.sessionToken === localToken,
      });

      if (localToken && data.sessionToken && data.sessionToken !== localToken) {
        debugLog('🚨 SESSION CONFLICT DETECTED — another device logged in');
        onConflict();
      }
    },
    (error) => {
      // Log loudly — if this fires on permission-denied, session enforcement is broken
      console.error('❌ [SESSION-MANAGER] Session listener FAILED:', error.code || error.message, error);
      if (typeof onError === 'function') {
        onError(error);
      }
    },
  );

  return unsubscribe;
};

/**
 * Clean up session state on sign-out.
 */
export const clearSession = (uid) => {
  if (!uid) return;
  debugLog('Clearing local session for uid:', uid);
  clearLocalSessionToken(uid);
};

// ---------------------------------------------------------------------------
// Activity heartbeat
// ---------------------------------------------------------------------------

// Module-level throttle: only write to Firestore at most once every 5 minutes
let _lastActivityWrite = 0;
const ACTIVITY_WRITE_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Update the `lastActiveAt` timestamp on the user's active session document
 * and the lightweight `userPresence` doc (used for cross-env visibility).
 * Throttled to one write per 5 minutes to avoid excessive Firestore operations.
 * Safe to call frequently — extra calls are silently dropped.
 *
 * @param {string}  uid        - Firebase Auth UID
 * @param {string}  [email]    - User email (enriches both docs for cross-env session matching)
 * @param {boolean} [force]    - Skip the 5-min throttle (use on page load / auth events)
 */
export const updateSessionActivity = async (uid, email = null, { force = false } = {}) => {
  if (!uid) return;

  const now = Date.now();
  if (!force && now - _lastActivityWrite < ACTIVITY_WRITE_INTERVAL_MS) return;
  _lastActivityWrite = now;

  const sessionRef  = getSessionDocRef(uid);
  const presenceRef = getPresenceDocRef(uid);

  // Build update payloads — email is merged in whenever available so that legacy
  // activeSessions docs (written before email was added) become matchable by email.
  const sessionUpdate = { lastActiveAt: serverTimestamp() };
  if (email) sessionUpdate.email = email;

  const presenceUpdate = { lastActiveAt: serverTimestamp(), env: CURRENT_ENV };
  if (email) presenceUpdate.email = email;

  // Use setDoc+merge for both writes so they succeed even if the doc is missing
  // (race condition on first load) instead of failing the entire Promise.all.
  const sessionWrite = setDoc(sessionRef, sessionUpdate, { merge: true }).catch((err) => {
    debugLog('Session heartbeat write failed:', err.code || err.message);
  });
  const presenceWrite = setDoc(presenceRef, presenceUpdate, { merge: true }).catch((err) => {
    debugLog('Presence heartbeat write failed:', err.code || err.message);
  });

  await Promise.all([sessionWrite, presenceWrite]);
  debugLog('Activity heartbeat written for uid:', uid);
};
